import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SCUIDownloadButton from '../../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUILoadingLinear, SCUIPagination } from '../../../../atomic/atoms';
import { CustomerCommand, SearchServiceCommand } from '../../../../services/command';
import { CustomerQuery, ProductSearchServiceQuery, ReturnQuery } from '../../../../services/query';
import Helper from '../../../../utils/helper';
import { SCUIGeneralDrawer, SCUITextFieldWithSelectBox } from '../../../../atomic/molecules';
import { SCUIOrderDetailDrawerContent } from '../../../Orders/components';
import { NoResultFoundComponent } from '../NoResultFoundComponent/NoResultFoundComponent';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { COIP } from './customerOrderInformationPaper.classes';
import { CustomerOrderRow } from './CustomerOrderRow';
import {
    applyCurrencyToOrderRows,
    buildOrderQueryParams,
    flattenOrdersForExcel,
    mapRptTopicOptions,
    parseCustomerOrderItems,
    parseSpecialAndColorCode,
} from './customerOrderInformationPaper.helpers';

const currency = import.meta.env.VITE_CURRENCY;

export const CustomerOrderInformationPaper: FC<any> = ({ datas, urlParams }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [customerOrders, setCustomerOrders] = useState<any>();
    const [rawCustomerOrders, setRawCustomerOrders] = useState<any[]>([]);
    const [recordCount, setRecordCount] = useState(0);
    const [orderForDrawerContent, setOrderForDrawerContent] = useState<any>();
    const [pageLoading, setPageLoading] = useState(false);
    const [documentType, setDocumentType] = useState(1);
    const [searchedText, setSearchedText] = useState<null | string>(null);
    const [barcodeArray, setBarcodeArray] = useState<any>([]);
    const [rptTopic, setRptTopic] = useState<any>();
    const [customerPageRule, setCustomerPageRule] = useState<any>();
    const [canCreateRefund, setCanCreateRefund] = useState<boolean>(true);

    const { t } = useTranslation();

    const isDarkMode = useSelector((state: any) => state.darkMode.isDarkMode);

    const [obj, setObj] = useState<any>({
        value: -1,
        text: t('general.all'),
        days: -1,
    });

    const barcode = urlParams.get('Barcodes') ? urlParams.get('Barcodes') : '';

    const onDrawerOpen = () => setDrawerOpen(true);

    const onDrawerClose = () => setDrawerOpen(false);

    const handleChangeRows = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const GetRptTopic = async () => {
        try {
            const res: any = await CustomerQuery.GetRptTopic();
            if (res.data) {
                const parseData = mapRptTopicOptions(res.data);
                setRptTopic(parseData && [...[{ value: -1, text: t('general.all'), days: -1 }], ...parseData]);
            } else {
                Swal.fire('Service Error', res.errorMessage, 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };
    const GetCustomerPageRule = async () => {
        try {
            const res: any = await CustomerQuery.GetCustomerSubMenuFromRule();
            if (res.data) {
                setCustomerPageRule(res.data);
            }
        } catch (error: any) {
            console.log('Hata :>> ', error);
        }
    };
    const GetOrderByCustomerId = async () => {
        setPageLoading(true);
        const params = buildOrderQueryParams({
            customerId: datas.customer.customerId,
            rowsPerPage,
            page,
            searchedText,
            documentType,
            barcode,
            barcodeArray,
            contactReasonDays: obj.days,
        });

        try {
            const res: any = await CustomerCommand.GetOrderByCustomerId(params);
            if (res) {
                setRawCustomerOrders(res.data || []);
                const parseData = applyCurrencyToOrderRows(parseCustomerOrderItems(res.data || []), currency);
                setCustomerOrders(parseData);
                setRecordCount(res.count);
            }
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
        } finally {
            setLoading(false);
            setPageLoading(false);
        }
    };

    const GoToProduct = async (barcode: string) => {
        try {
            const res: any = await ProductSearchServiceQuery.GetOptionIds({
                searchType: 3,
                searchKeys: [barcode],
            });
            Helper.openInNewTab('/productDetail/' + res[0].optionId);
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
        }
    };

    const handleClick = (popupState: any, tur: string, row: any) => {
        popupState.close();
        switch (tur) {
            case 'showDetail': {
                setOrderForDrawerContent(row);
                onDrawerOpen();
                break;
            }
            case 'gotoDetail': {
                window.open('/orderdetail?OrderNumber=' + row.orderNumber, '_blank');
                break;
            }
            case 'createReturn': {
                window.open('/createReturn?orderNumber=' + row.orderNumber, '_blank');
                break;
            }
        }
    };

    function ChoseSelect() {
        setPage(0);
        if (documentType === 1) {
            GetOrderByCustomerId();
        } else if (documentType === 2) {
            setPage(0);
            GetOrderWthSpecialColorCode(searchedText || '');
        }
    }

    async function GetOrderWthSpecialColorCode(data: any) {
        setLoading(true);

        const specialAndColor = parseSpecialAndColorCode(data);
        if (!specialAndColor) {
            setLoading(false);
            return;
        }

        try {
            const barcodeResponse = await SearchServiceCommand.GetBarcodeBySpecialAndColorCodeList({
                specialCodeList: [specialAndColor],
            });

            if (!barcodeResponse.data?.length) {
                Swal.fire('Bu özel-renk koduna ait barkod bulunamadı.', ' ', 'error');
                setCustomerOrders([]);
                setRawCustomerOrders([]);
                setRecordCount(0);
                setLoading(false);
                return;
            }

            const barcodesArray = barcodeResponse.data.map((item: any) => item.barcode);
            setBarcodeArray(barcodesArray);

            const orderResponse = await CustomerCommand.GetOrderByCustomerId({
                CustomerId: datas.customer.customerId,
                take: rowsPerPage,
                skip: 0,
                Barcodes: barcodesArray,
            });

            setLoading(true);

            if (orderResponse.formattedServiceResult === null) {
                setCustomerOrders([]);
                setRawCustomerOrders([]);
                setRecordCount(0);
                setLoading(false);
                Swal.fire('Veri bulunamadı.', ' ', 'error');
                return;
            }

            setRawCustomerOrders(orderResponse.data || []);
            setCustomerOrders(applyCurrencyToOrderRows(parseCustomerOrderItems(orderResponse.data || []), currency));
            setRecordCount(orderResponse.count);
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
        } finally {
            setLoading(false);
            setPageLoading(false);
        }
    }

    useEffect(() => {
        const isNumber = /^\d+$/.test(searchedText || '');
        if (searchedText === null || searchedText === '') {
            GetRptTopic();
            GetOrderByCustomerId();
        } else if (isNumber) {
            setDocumentType(1);
        } else {
            setDocumentType(2);
        }
    }, [searchedText]);

    useEffect(() => {
        if (documentType === 2) {
            GetOrderWthSpecialColorCode(searchedText || '');
        }
        GetOrderByCustomerId();
    }, [page, rowsPerPage, obj]);

    useEffect(() => {
        GetCustomerPageRule();
    }, []);

    useEffect(() => {
        const checkCanCreateRefund = async () => {
            try {
                const response = await ReturnQuery.CanStoreUserCreateRefund();
                setCanCreateRefund(response?.data ?? true);
            } catch (error) {
                console.error('Error checking refund permission:', error);
                setCanCreateRefund(true);
            }
        };
        checkCanCreateRefund();
    }, []);

    const excelDownloadData = useMemo(
        () => (rawCustomerOrders.length ? flattenOrdersForExcel(rawCustomerOrders) : []),
        [rawCustomerOrders]
    );

    return (
        <Grid className={COIP.page}>
            <Grid
                className={COIP.searchRow}
                container
                sx={{ marginBottom: '2rem' }}
                spacing={2}
                size={{
                    xl: 12,
                    lg: 12,
                    xs: 12,
                    md: 12,
                }}
            >
                <Grid
                    size={{
                        xl: 6,
                        lg: 6,
                        md: 12,
                        xs: 12,
                    }}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Box className={COIP.inputCompositeSearch} sx={{ width: '100%' }}>
                        <SCUITextFieldWithSelectBox
                            placeholder={' '}
                            buttonText={t('general.search')}
                            sx={{ width: '100%' }}
                            selectList={[
                                { value: 1, text: t('searchService.barcode') },
                                { value: 2, text: t('searchService.specialAndColorCode') },
                            ]}
                            onChangeSelect={(event: any) => {
                                setDocumentType(event.target.value as number);
                            }}
                            selectValue={documentType}
                            onChangeTextField={(event: any) => {
                                setSearchedText(event.target.value.trim());
                            }}
                            onClickButton={() => {
                                setPage(0);
                                ChoseSelect();
                            }}
                            onKeyPressTextField={(e: any) => {
                                if (e.key === 'Enter') {
                                    setPage(0);
                                    e.preventDefault();
                                    ChoseSelect();
                                }
                            }}
                        />
                    </Box>
                </Grid>
                {customerPageRule?.showReasonContact && (
                    <Grid
                        size={{
                            xl: 6,
                            lg: 6,
                            md: 12,
                            xs: 12,
                        }}
                    >
                        <FormControl className={COIP.fieldContactReason} style={{ width: '100%' }}>
                            <InputLabel id="labelApp">{'Temas Nedeni'}</InputLabel>
                            <Select
                                className={COIP.inputSelectContactReason}
                                label={'Temas Nedeni'}
                                labelId="labelApp"
                                id="parentMenuLabel"
                                defaultValue={-1}
                                value={obj}
                                onChange={(event: any) => {
                                    setObj(event.target.value);
                                    setPage(0);
                                }}
                                sx={{ height: '40px' }}
                                fullWidth
                                renderValue={(selected) => selected.text}
                            >
                                {rptTopic &&
                                    rptTopic.map((comp: any) => (
                                        <MenuItem key={comp.value} value={comp}>
                                            {comp.text}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>
            <Paper
                className={COIP.mainPaper}
                style={{
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 1,
                }}
            >
                <Grid container justifyContent={'space-between'} sx={{ margin: '1rem' }}>
                    <SCUIGeneralDrawer
                        open={drawerOpen}
                        anchor="right"
                        width={'350px'}
                        onClose={(event: any, reason: any) => {
                            if (reason === 'backdropClick') {
                                setDrawerOpen(false);
                            }
                        }}
                    >
                        <SCUIOrderDetailDrawerContent detail={orderForDrawerContent} onClose={onDrawerClose} />
                    </SCUIGeneralDrawer>

                    <SCUILoadingLinear display={loading} />
                    {customerOrders && customerOrders.length > 0 ? (
                        <Grid
                            className={COIP.tableSection}
                            sx={{ marginRight: '1.5rem' }}
                            size={{
                                xl: 12,
                                lg: 12,
                                md: 12,
                                xs: 12,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <SCUIDownloadButton
                                    position="right"
                                    excelData={{
                                        excelDownloadData,
                                        excelDownloadFolderName: t('customerInformation.followCustomer'),
                                        excelDownloadButtonText: t('general.exportToExcel'),
                                    }}
                                />
                            </Box>
                            <TableContainer className={COIP.tableContainer} component={Paper}>
                                <Table aria-label="collapsible table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell align="center">{t('general.orderNo')}</TableCell>
                                            <TableCell align="center">{t('general.orderDate')}</TableCell>
                                            <TableCell align="center">{t('return.deliveryDate')}</TableCell>
                                            <TableCell align="center">{t('customerInformation.elapsedTime')}</TableCell>
                                            <TableCell align="center">{t('general.cost')}</TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    maxWidth: 110,
                                                    width: 110,
                                                }}
                                            >
                                                {t('general.status')}
                                            </TableCell>
                                            <TableCell align="center">{t('customerInformation.paymentType')}</TableCell>
                                            <TableCell align="center">
                                                {t('customerInformation.shippingNumber')}
                                            </TableCell>
                                            <TableCell align="center">{t('customerInformation.orderType')}</TableCell>
                                            <TableCell align="center">{t('general.operation')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {customerOrders.map((row: any) => (
                                            <CustomerOrderRow
                                                key={row.rowId}
                                                order={row}
                                                isDarkMode={isDarkMode}
                                                canCreateRefund={canCreateRefund}
                                                onGoToProduct={GoToProduct}
                                                onRowMenuClick={handleClick}
                                            />
                                        ))}
                                    </TableBody>
                                    <SCUIPagination
                                        rowsPerPageOptions={[10, 20, 50]}
                                        count={recordCount}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onRowsPerPageChange={handleChangeRows}
                                        setPage={setPage}
                                    />
                                </Table>
                            </TableContainer>
                        </Grid>
                    ) : !pageLoading ? (
                        <NoResultFoundComponent errorText={'Sipariş Bilgisi Bulunamadı'} />
                    ) : (
                        <SCUILoadingLinear display={pageLoading} />
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
};
